const SEEDE_RENDER_LOG_LEVEL = '__SEEDE_RENDER_LOG_LEVEL__';

const LOG_LEVELS = {
  none: 0,
  error: 1,
  warn: 2,
  info: 3,
} as const;

type LogLevelName = keyof typeof LOG_LEVELS;

/**
 * 获取当前设置的日志级别。
 * 在开发环境中，默认级别为 'error'。
 * 开发者可以在浏览器控制台通过设置 globalThis.__SEEDE_RENDER_LOG_LEVEL__ 来覆盖它。
 * e.g., globalThis.__SEEDE_RENDER_LOG_LEVEL__ = 'info'
 */
function getLogLevel(): number {
  let level: LogLevelName = 'error';
  if (typeof localStorage !== 'undefined') {
    level = (localStorage.getItem(SEEDE_RENDER_LOG_LEVEL) ?? 'error') as LogLevelName;
  }

  if (typeof globalThis !== 'undefined') {
    level = ((globalThis as any)[SEEDE_RENDER_LOG_LEVEL] ?? level) as LogLevelName;
  }

  return LOG_LEVELS[level] ?? LOG_LEVELS.error;
}

const noop = () => {};

const createLogger = () => {
  if (!import.meta.env.DEV) {
    const noops = { info: noop, warn: noop, error: noop };
    return {
      ...noops,
      prefix: (_tag: string) => noops,
    };
  }

  const SEEDE_RENDERER_LOG_PREFIX = 'Seede-Renderer';

  const STYLE_PREFIX = 'color: rgb(115, 249, 155); font-weight: 700';
  const STYLE_TAG = 'color: rgb(140, 170, 215); font-weight: 600';
  const STYLE_MSG = 'color: rgba(0,0,0,0.65); font-weight: 400';

  const splitMsgs = (args: unknown[]) => {
    const texts: string[] = [];

    for (const a of args) {
      if (
        typeof a === 'string' ||
        typeof a === 'number' ||
        typeof a === 'boolean' ||
        a === null ||
        a === undefined
      ) {
        texts.push(String(a));
      } else {
        let content: string;

        try {
          content = JSON.stringify(a);
        } catch {
          content = String(a);
        }

        texts.push(content);
      }
    }
    return { text: texts.join(' ') };
  };

  const formatParts = (
    tag?: string,
    project: string = SEEDE_RENDERER_LOG_PREFIX,
    msgs: string[] = []
  ) => {
    const { text } = splitMsgs(msgs);

    let formattedContent = `%c[${project}]`;
    const styles: string[] = [STYLE_PREFIX];

    if (tag && tag.trim()) {
      formattedContent += `%c(${tag.trim()})`;
      styles.push(STYLE_TAG);
    }

    if (text) {
      formattedContent += `%c ${text}`;
      styles.push(STYLE_MSG);
    }

    return { content: formattedContent, styles };
  };

  const makeLoggerFns = (tag?: string, project?: string) => {
    const info = (...args: string[]) => {
      if (getLogLevel() >= LOG_LEVELS.info) {
        const { content, styles } = formatParts(tag, project, args);
        console.info(content, ...styles);
      }
    };

    const warn = (...args: string[]) => {
      if (getLogLevel() >= LOG_LEVELS.warn) {
        const { content, styles } = formatParts(tag, project, args);
        console.warn(content, ...styles);
      }
    };

    const error = (...args: string[]) => {
      if (getLogLevel() >= LOG_LEVELS.error) {
        const { content, styles } = formatParts(tag, project, args);
        console.error(content, ...styles);
      }
    };

    return { info, warn, error };
  };

  return {
    ...makeLoggerFns(undefined, SEEDE_RENDERER_LOG_PREFIX),
    prefix: (tag: string) => makeLoggerFns(tag),
  };
};

export const { info, warn, error, prefix } = createLogger();
